import React, { useContext, useEffect, useRef, useState } from 'react'
import StepWizard, { StepWizardChildProps } from 'react-step-wizard'
import { Box } from 'rebass'
import { useSTheme } from 'styles/theme'
import HouseholdIncomeIntroduction from './HouseholdIncomeIntroduction'
import HouseholdIncomMembers from './HouseholdIncomeMembers'
import HouseholdIncomeMembersDOB from './HouseholdIncomeMembersDOB'
import HouseholdIncomeBlindDisabledChoice from './HouseholdIncomeBlindDisabledChoice'
import HouseholdIncomeBlindDisabledMembers from './HouseholdIncomeBlindDisabledMembers'
import HouseholdIncomeChoice from './HouseholdIncomeChoice'
import HouseholdIncomeEarnedUnearned from './HouseholdIncomeEarnedUnearned'
import HouseholdIncomeEligibility from './HouseholdIncomeEligibility'
import HouseholdIncomeReview from './HouseholdIncomeReview'
import { ApplicationContext } from 'v2/context/application'
import { useRouter } from 'next/router'

interface Props {
    setCurrentStep?: (number) => void
    previousStep: (number) => void
    onSuccess: (string) => void
    fontFamily: any
    fromBackButton: boolean
    updatePercentage: any
}

const HouseholdIncomeWizardContainer: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)
    const router = useRouter()

    const [currentStep, setCurrentStep] = useState<number>(props.fromBackButton ? 7 : 1)
    const theme = useSTheme()
    const [onSaveAndContinueBlindDisabledChoice, setOnSaveAndContinueBlindDisabledChoice] = useState(null)
    const [onSaveAndContinueHouseholdIncomeChoice, setOnSaveAndContinueHouseholdIncomeChoice] = useState(null)

    const longBeachFontFamily = theme.fonts.longBeach
    const parentFlowPrevious = props.previousStep

    useEffect(() => {
        if (applicationContext.application.id) {
            router.replace(
                `/${window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment}/apply/${
                    applicationContext.application.id
                }/household`
            )
        }
    }, [])

    return (
        <Box>
            <StepWizard
                initialStep={currentStep}
                transitions={{
                    enterRight: '',
                    enterLeft: '',
                    exitRight: '',
                    exitLeft: '',
                }}
                isLazyMount={true}
                onStepChange={(stepInfo: { previousStep: number; activeStep: number }) => {
                    setCurrentStep(stepInfo.activeStep)
                }}
            >
                <HouseholdIncomeIntroduction
                    fontFamily={props.fontFamily}
                    onNextStep={props.nextStep}
                    onPrevious={parentFlowPrevious}
                    updatePercentage={props.updatePercentage}
                />
                <HouseholdIncomMembers
                    personId={applicationContext.profile.personId}
                    applicationId={applicationContext.application.id}
                    fontFamily={longBeachFontFamily}
                    onNextStep={props.nextStep}
                    onPrevious={props.previousStep}
                    updatePercentage={props.updatePercentage}
                />
                <HouseholdIncomeMembersDOB
                    personId={applicationContext.profile.personId}
                    applicationId={applicationContext.application.id}
                    fontFamily={longBeachFontFamily}
                    onNextStep={props.nextStep}
                    onPrevious={props.previousStep}
                    updatePercentage={props.updatePercentage}
                />
                <HouseholdIncomeBlindDisabledChoice
                    personId={applicationContext.profile.personId}
                    applicationId={applicationContext.application.id}
                    fontFamily={longBeachFontFamily}
                    onSaveAndContinue={setOnSaveAndContinueBlindDisabledChoice}
                    onNextStep={props.nextStep}
                    onPrevious={props.previousStep}
                    previousSelection={onSaveAndContinueBlindDisabledChoice}
                    updatePercentage={props.updatePercentage}
                />
                {onSaveAndContinueBlindDisabledChoice && (
                    <HouseholdIncomeBlindDisabledMembers
                        personId={applicationContext.profile.personId}
                        applicationId={applicationContext.application.id}
                        fontFamily={longBeachFontFamily}
                        onNextStep={props.nextStep}
                        onPrevious={props.previousStep}
                        updatePercentage={props.updatePercentage}
                    />
                )}
                <HouseholdIncomeChoice
                    personId={applicationContext.profile.personId}
                    applicationId={applicationContext.application.id}
                    onSaveAndContinue={setOnSaveAndContinueHouseholdIncomeChoice}
                    previouslySelectedMembers={onSaveAndContinueHouseholdIncomeChoice}
                    fontFamily={longBeachFontFamily}
                    onNextStep={props.nextStep}
                    onPrevious={props.previousStep}
                    updatePercentage={props.updatePercentage}
                />
                {(onSaveAndContinueHouseholdIncomeChoice?.applicantSelected ||
                    onSaveAndContinueHouseholdIncomeChoice?.membersWithIncome.length > 0) && (
                    <HouseholdIncomeEarnedUnearned
                        personId={applicationContext.profile.personId}
                        applicationId={applicationContext.application.id}
                        previouslySelectedMembers={onSaveAndContinueHouseholdIncomeChoice}
                        fontFamily={longBeachFontFamily}
                        onNextStep={props.nextStep}
                        onPrevious={props.previousStep}
                        updatePercentage={props.updatePercentage}
                    />
                )}
                <HouseholdIncomeEligibility
                    applicationId={applicationContext.application.id}
                    fontFamily={longBeachFontFamily}
                    onNextStep={props.nextStep}
                    onPrevious={props.previousStep}
                    updatePercentage={props.updatePercentage}
                />
                <HouseholdIncomeReview
                    applicationId={applicationContext.application.id}
                    personId={applicationContext.profile.personId}
                    fontFamily={longBeachFontFamily}
                    onNextStep={props.onSuccess}
                    onPrevious={props.previousStep}
                    updatePercentage={props.updatePercentage}
                />
            </StepWizard>
        </Box>
    )
}

export default HouseholdIncomeWizardContainer
